import { useRef, useCallback, useState } from 'react';

/**
 *
 * @param {(NodeJS.Timeout|undefined)} val
 */
function setTimeoutStateHandler(val) {
  /** @param {(NodeJS.Timeout|undefined)} prev */
  return prev => {
    if (prev) {
      clearTimeout(prev);
    }

    return val;
  };
}

/**
 *
 * @param {object} params
 * @param {number=} params.waitTime
 */
export default function useHoverDropdown({ waitTime = 500 } = {}) {
  /**
   * this will be wrapped around the changes done on mouseenter/mouseleave,
   * and resolved when setIsShowing() state callback fns are triggered
   * that way, we can wait for existing state updates to resolve before
   * handling external change events like click, etc
   */
  let waitForStateChange = Promise.resolve();

  const [alwaysHide, setAlwaysHide] = useState(false);
  const [hoverCount, setHoverCount] = useState(0);
  /**  @type {[(NodeJS.Timeout|undefined), React.Dispatch<React.SetStateAction<(NodeJS.Timeout|undefined)>>]} mouseLeaveTimeoutState */
  const [mouseLeaveTimeout, setMouseLeaveTimeout] = useState();
  const [isShowing, setIsShowing] = useState(false);

  /** @param {boolean} immediate */
  function closeDropdown(immediate) {
    if (immediate) {
      setIsShowing(false);
      setMouseLeaveTimeout(setTimeoutStateHandler(undefined));
    } else {
      const timeout = setTimeout(() => {
        if (hoverCount === 0) {
          setIsShowing(false);
        }
      }, waitTime);
      setMouseLeaveTimeout(setTimeoutStateHandler(timeout));
    }
  }

  function openDropdown() {
    setIsShowing(true);
    setMouseLeaveTimeout(setTimeoutStateHandler(undefined));
  }

  /** @param {boolean} enabled */
  function toggleHoverHandling(enabled) {
    setAlwaysHide(!enabled);
  }

  function useHoverRef() {
    let mouseEnterListener = () => {};
    let mouseLeaveListener = () => {};

    /** @type {React.MutableRefObject<HTMLElement|null>} ref */
    const ref = useRef(null);
    const setRef = useCallback(
      /** @param {HTMLElement|null} el */
      el => {
        if (ref.current) {
          ref.current.removeEventListener('mouseenter', mouseEnterListener);
          ref.current.removeEventListener('mouseleave', mouseLeaveListener);
        }

        if (el && !alwaysHide) {
          mouseEnterListener = () => {
            waitForStateChange = new Promise(resolve => {
              setHoverCount(prev => {
                resolve();
                openDropdown();
                return prev + 1;
              });
            });
          };
          el.addEventListener('mouseenter', mouseEnterListener);

          /**
           * TODO: investigate how we can handle when mouseleave is result of right-click. see CS-2465
           */
          mouseLeaveListener = () => {
            waitForStateChange = new Promise(resolve => {
              setHoverCount(prev => {
                resolve();
                if (prev - 1 === 0) {
                  closeDropdown(false);
                }
                return prev - 1;
              });
            });
          };
          el.addEventListener('mouseleave', mouseLeaveListener);
        }

        ref.current = el;
      },
      [alwaysHide],
    );
    return setRef;
  }

  return {
    isShowing,
    toggleHoverHandling,
    useHoverRef,
    closeDropdown: /** @param {boolean} immediate */ immediate =>
      waitForStateChange.then(() => closeDropdown(immediate)),
    openDropdown: () => waitForStateChange.then(openDropdown),
  };
}
